import React, { type ReactNode } from 'react';
import { z } from 'zod';

import type { PositionInput } from '#core/schema/defs.ts';

import { type DashboardGridData, type DashboardGridItem, renderDashboard } from './reconciler/serialize.ts';
import { logGridCorrections, validateAndCorrectGrid } from './validation.ts';

export function isComponentWithPosition(element: unknown): element is React.ReactElement<{ position: PositionInput }> {
  if (!React.isValidElement(element)) {
    return false;
  }
  const props = element.props as Record<string, unknown>;
  return typeof props.position === 'object' && props.position !== null;
}

export function hasFlattenedChildren(children: ReactNode): boolean {
  const childArray = React.Children.toArray(children);
  return childArray.some(isComponentWithPosition);
}

export function buildFromFlattenedChildren(
  children: ReactNode,
  layout: { rows: number; columns: number },
  gridTitle?: string,
): DashboardGridData {
  const childArray = React.Children.toArray(children);
  const items: DashboardGridItem[] = [];

  for (const child of childArray) {
    if (!React.isValidElement(child)) {
      continue;
    }

    const props = child.props as Record<string, unknown>;
    const positionProp = props.position;

    if (!positionProp || typeof positionProp !== 'object') {
      console.warn('DashboardGrid: Child element missing valid position prop, skipping:', child.type);
      continue;
    }

    const elementType = typeof child.type === 'string' ? child.type : '';
    const match = elementType.match(/^dashboard-(.+)$/);
    const componentType = match?.[1] ?? elementType;

    const { position: _, ...componentProps } = props;

    const position = positionProp as Record<string, unknown>;
    items.push({
      position: {
        row: typeof position.row === 'number' ? position.row : 1,
        column: typeof position.column === 'number' ? position.column : 1,
        rowSpan: typeof position.rowSpan === 'number' ? position.rowSpan : 1,
        columnSpan: typeof position.columnSpan === 'number' ? position.columnSpan : 1,
      },
      component: { ...componentProps, type: componentType },
    });
  }

  return {
    layout,
    items,
    ...(gridTitle && { title: gridTitle }),
  };
}

export const DashboardGridPropsSchema = z
  .object({
    data: z.custom<DashboardGridData>().optional(),
    children: z.custom<ReactNode>().optional(),
    rows: z.number().optional(),
    columns: z.number().optional(),
    title: z.string().optional(),
  })
  .transform((props): DashboardGridData => {
    const { data, children, rows, columns, title } = props;
    const hasData = data !== undefined;
    const hasChildren = children !== undefined && children !== null;
    const hasLayoutProps = rows !== undefined || columns !== undefined;

    if (hasData) {
      const result = validateAndCorrectGrid(data.items, data.layout);
      if (result.corrections.length > 0) {
        logGridCorrections(result.corrections);
      }
      return {
        ...data,
        layout: result.layout,
        items: result.items,
      };
    }

    if (!hasChildren) {
      throw new Error('DashboardGrid: Must specify either "data" or "children" prop.');
    }

    if (hasLayoutProps && hasFlattenedChildren(children)) {
      const layout = {
        rows: rows ?? 3,
        columns: columns ?? 12,
      };
      return buildFromFlattenedChildren(children, layout, title);
    }

    if (!React.isValidElement(children)) {
      throw new Error(`DashboardGrid: children must be a single React element. Got: ${typeof children}`);
    }
    return renderDashboard(children);
  });
